import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { startAuthentication } from "@simplewebauthn/browser";
import { webauthnApi } from "../api/webauthnApi";
import { finalizeWebauthnLogin } from "../../../redux/actions/auth";
import { WEBAUTHN_MOCK_CEREMONY } from "../config";
import { usePreauthExpiryGuard } from "../usePreauthExpiryGuard";
import { getDeviceFingerprint } from "../deviceFingerprint";

/**
 * Assertion ceremony for a known browser: options -> navigator.credentials.get
 * -> verify -> finalize session. Must be triggered from a native click handler
 * (Safari user-gesture rule); the only async step before startAuthentication
 * is the options fetch, as required.
 */
export function useAuthenticateCeremony() {
  const dispatch = useDispatch();
  const guardPreauth = usePreauthExpiryGuard();
  const ticket = useSelector((s) => s.auth.webauthn?.preauth_ticket);

  return useMutation(
    async () => {
      const optionsJSON = await webauthnApi.authOptions(ticket);
      const assertion = WEBAUTHN_MOCK_CEREMONY
        ? { mock: true }
        : await startAuthentication({ optionsJSON });
      // Fingerprint AFTER the ceremony so no async sits between the gesture and
      // startAuthentication (Safari); memoized so this await is instant.
      const fingerprint = await getDeviceFingerprint();
      return webauthnApi.authVerify(assertion, fingerprint, ticket);
    },
    {
      onSuccess: (tokenPayload) => {
        if (tokenPayload && tokenPayload.token) {
          dispatch(finalizeWebauthnLogin(tokenPayload));
        }
      },
      onError: (error) => guardPreauth(error),
    }
  );
}
